import type { SmartCreationReviewTarget } from "@adpilot/shared";
import { useEffect, useMemo, useState } from "react";
import { fetchSpCategoryBrands, searchSpTargetCategories } from "@/lib/campaign-creation-api";

interface CategoryDisplayNames {
  category?: string;
  brands: Record<string, string>;
}

type TargetDisplayNames = Record<string, CategoryDisplayNames>;

export function targetDisplayLabel(
  target: SmartCreationReviewTarget,
  names: TargetDisplayNames,
): string | null {
  if (target.type === "category") return names[target.categoryId]?.category ?? target.categoryId;
  if (target.type === "category_brand") {
    const category = names[target.categoryId];
    return `${category?.category ?? target.categoryId} / ${category?.brands[target.brandId] ?? target.brandId}`;
  }
  return null;
}

/** Display names are Profile-scoped hints; numeric IDs remain the saved targeting identity. */
export function useTargetDisplayNames(
  shopId: string | undefined,
  targets: readonly SmartCreationReviewTarget[],
) {
  const categories = useMemo(() => {
    const byCategory = new Map<string, Set<string>>();
    for (const target of targets) {
      if (target.type !== "category" && target.type !== "category_brand") continue;
      const brands = byCategory.get(target.categoryId) ?? new Set<string>();
      if (target.type === "category_brand") brands.add(target.brandId);
      byCategory.set(target.categoryId, brands);
    }
    return [...byCategory].map(([id, brands]) => ({ id, brandIds: [...brands] }));
  }, [targets]);
  const [names, setNames] = useState<TargetDisplayNames>({});
  const [loading, setLoading] = useState(() => Boolean(shopId && categories.length > 0));

  useEffect(() => {
    if (!shopId || categories.length === 0) {
      setNames({});
      setLoading(false);
      return;
    }
    let active = true;
    setNames({});
    setLoading(true);
    void Promise.all(
      categories.map(async ({ id, brandIds }) => {
        const [categoryResult, brandResult] = await Promise.all([
          searchSpTargetCategories(shopId, id, 1, 20).catch(() => null),
          brandIds.length > 0
            ? fetchSpCategoryBrands(shopId, id).catch(() => [])
            : Promise.resolve([]),
        ]);
        return [
          id,
          {
            category: categoryResult?.data.find((item) => item.id === id)?.name,
            brands: Object.fromEntries(
              brandResult
                .filter((item) => brandIds.includes(item.id))
                .map((item) => [item.id, item.name]),
            ),
          },
        ] as const;
      }),
    ).then((resolved) => {
      if (!active) return;
      setNames(Object.fromEntries(resolved));
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [shopId, categories]);

  return { names, loading };
}
